import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  SalesTransaction,
  SalesTransactionsParams,
  SalesTransactionsResponse,
  SalesTransactionsResult,
} from '../types/salesTransaction';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getSalesTransactions = async (
  params?: SalesTransactionsParams
): Promise<SalesTransactionsResult> => {
  const queryParams = new URLSearchParams();

  if (params?.type) {
    queryParams.set('type', params.type);
  }
  if (params?.status) {
    queryParams.set('status', params.status);
  }
  if (params?.customer_id) {
    queryParams.set('customer_id', String(params.customer_id));
  }
  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.search) {
    queryParams.set('search', params.search);
  }
  if (params?.paid_days !== undefined) {
    queryParams.set('paid_days', String(params.paid_days));
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SALES_TRANSACTIONS.GET_ALL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch sales transactions');
  }

  const result: SalesTransactionsResponse = await response.json();
  return {
    summary: result.summary,
    transactions: result.data,
    meta: result.meta,
    links: result.links,
  };
};

export const fetchAllSalesTransactions = async (
  params: Omit<SalesTransactionsParams, 'page'>
): Promise<SalesTransaction[]> => {
  const all: SalesTransaction[] = [];
  let page = 1;
  let lastPage = 1;

  do {
    const result = await getSalesTransactions({ ...params, page });
    all.push(...result.transactions);
    lastPage = result.meta.last_page;
    page += 1;
  } while (page <= lastPage);

  return all;
};
